import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as testRunApi from '../services/testRunService';
import { testCaseKeys } from './useTestCases';

export const testRunKeys = {
  active: (projectId: string, specId: string) =>
    ['testRuns', projectId, specId, 'active'] as const,
  list: (projectId: string, specId: string) =>
    ['testRuns', projectId, specId] as const,
  detail: (projectId: string, specId: string, runId: string) =>
    ['testRuns', projectId, specId, runId] as const,
  results: (projectId: string, runId: string) =>
    ['testRunResults', projectId, runId] as const,
};

/** TestRun 목록 조회 */
export function useRunList(projectId: string, specId: string) {
  return useQuery({
    queryKey: testRunKeys.list(projectId, specId),
    queryFn: () => testRunApi.fetchRunList(projectId, specId),
    enabled: !!projectId && !!specId,
  });
}

/** TestRun 생성 mutation */
export function useCreateTestRun(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (specId: string) => testRunApi.createTestRun(projectId, specId),
    onSuccess: (_data, specId) => {
      queryClient.invalidateQueries({ queryKey: testRunKeys.list(projectId, specId) });
      queryClient.invalidateQueries({ queryKey: testRunKeys.active(projectId, specId) });
      queryClient.invalidateQueries({ queryKey: testCaseKeys.all(projectId) });
    },
  });
}

/** TestRun 상세 + 대시보드 조회 */
export function useRunDetail(projectId: string, specId: string, runId: string) {
  return useQuery({
    queryKey: testRunKeys.detail(projectId, specId, runId),
    queryFn: () => testRunApi.fetchRunDetail(projectId, specId, runId),
    enabled: !!projectId && !!specId && !!runId,
  });
}

/** TestRunResult 목록 조회 */
export function useRunResults(projectId: string, runId: string) {
  return useQuery({
    queryKey: testRunKeys.results(projectId, runId),
    queryFn: () => testRunApi.fetchResults(projectId, runId),
    enabled: !!projectId && !!runId,
  });
}

/** 결과 업데이트 mutation */
export function useUpdateResult(projectId: string, specId: string, runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { resultId: string; data: { status: string; actualResult?: string } }) =>
      testRunApi.updateResult(projectId, runId, params.resultId, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testRunKeys.detail(projectId, specId, runId) });
      queryClient.invalidateQueries({ queryKey: testRunKeys.results(projectId, runId) });
    },
  });
}

/** TestRun 상태 변경 mutation (COMPLETED / ABORTED) */
export function useUpdateRunStatus(projectId: string, specId: string, runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: 'COMPLETED' | 'ABORTED') =>
      testRunApi.updateRunStatus(projectId, specId, runId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testRunKeys.detail(projectId, specId, runId) });
      queryClient.invalidateQueries({ queryKey: testRunKeys.active(projectId, specId) });
      queryClient.invalidateQueries({ queryKey: testRunKeys.list(projectId, specId) });
    },
  });
}

/** FAIL 이슈 처리 mutation (CREATE_NEW / REOPEN) */
export function useHandleFailAction(projectId: string, specId: string, runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { resultId: string; data: { action: 'CREATE_NEW' | 'REOPEN'; issueId?: string; priority?: string; assigneeId?: string } }) =>
      testRunApi.handleFailAction(projectId, runId, params.resultId, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testRunKeys.detail(projectId, specId, runId) });
      queryClient.invalidateQueries({ queryKey: testRunKeys.results(projectId, runId) });
    },
  });
}
